/**
 * Familia de perfiles por cadencia de reporte, y su selección automática.
 *
 * Por qué existe: los umbrales del perfil original (`guardia-lab v3`) están
 * calibrados para telemetría de 1 a 2 segundos. A 10 segundos, alcanzar
 * 2,5 m/s² exige variar 90 km/h en esos 10 segundos, cosa que ningún vehículo
 * hace — así que los detectores quedan ciegos y el informe sale en ceros
 * (ADR-007).
 *
 * La flota de Grupo Arcos no reporta a una sola cadencia: hay equipos a 5 s y
 * equipos a 10 s. Un umbral único es equivocado para todos menos uno.
 *
 * La solución NO es bajar el umbral hasta que aparezcan episodios. Es reconocer
 * que a cada cadencia se está midiendo **una magnitud distinta**:
 *
 *   a 1–2 s   →  aceleración instantánea, o casi
 *   a 5 s     →  aceleración media sostenida durante 5 s
 *   a 10 s    →  aceleración media sostenida durante 10 s
 *
 * Son tres cosas diferentes y comparables solo dentro de su propio régimen. Por
 * eso cada perfil dice qué mide, y por eso el perfil entra en la firma del
 * análisis: dos informes calculados con perfiles distintos NO son comparables,
 * aunque los datos vengan del mismo vehículo.
 *
 * Los umbrales de los perfiles derivados están en `DRAFT` y **sin calibrar**
 * hasta que el laboratorio (`core/calibration/`) los fije contra recorridos
 * reales. Mientras tanto, están declarados como provisionales y el informe lo
 * dice. Un número inventado con buena letra sigue siendo inventado.
 */
import type { TunableThresholds } from "../contracts/api.ts";

import { LAB_PROFILE, REPEATED_FIX_MINIMUM_SPEED_KMH } from "./lab-profile.ts";
import { CADENCE_REGIME_HIGH_MEDIUM_SECONDS, CADENCE_REGIME_MEDIUM_LOW_SECONDS } from "./cadence-regime.ts";
import { MANEUVER_CADENCE_CEILING_SECONDS } from "./sampling-quality.ts";

export type CadenceRegime = "HIGH" | "MEDIUM" | "LOW";

/**
 * Perfil de análisis: la misma forma de `LAB_PROFILE` pero con los tipos
 * ensanchados. `LAB_PROFILE` es `as const`, así que cada umbral tiene su propio
 * tipo literal (`2.5`, no `number`) — perfecto para que nadie lo mute por
 * accidente, imposible para declarar una familia de perfiles con otros valores.
 */
export type AnalysisProfile = {
  profileId: string;
  version: number;
  status: string;
  timezone: string;
  quality: { [K in keyof typeof LAB_PROFILE.quality]: number };
  trips: { [K in keyof typeof LAB_PROFILE.trips]: number };
  longitudinal: { [K in keyof typeof LAB_PROFILE.longitudinal]: number };
  turns: { [K in keyof typeof LAB_PROFILE.turns]: number };
  elevatedSpeed: {
    operationalSpeedLimitKmh: number;
    operationalSpeedExitKmh: number;
    minimumSegmentDurationSeconds: number;
    sustainedWindowSeconds: number;
    minimumSegmentDistanceMeters: number;
    thresholdSource: string;
  };
  severity: {
    acceleration: readonly [number, number, number];
    braking: readonly [number, number, number];
    yawRate: readonly [number, number, number];
    turnReferenceSpeed: readonly [number, number, number];
    elevatedSpeedExcessPercent: readonly [number, number, number];
    classification: string;
  };
} & {
  /** Régimen de cadencia para el que se calibró. */
  regime: CadenceRegime;
  /** Intervalo mediano, en segundos, al que aplica. `null` = cualquiera. */
  appliesToIntervalSeconds: readonly [number, number] | null;
  /** Qué mide de verdad este perfil. Va impreso en el informe. */
  measures: string;
  /** `CALIBRATED` o `UNCALIBRATED`. Ver `core/calibration/`. */
  calibration: "CALIBRATED" | "UNCALIBRATED";
  /**
   * Con qué flota se comprobó un umbral común de cadencia (ADR-009). Solo la
   * llevan los perfiles de alcance CADENCIA guardados con la verificación de
   * flota; los del código y los de un solo vehículo no la tienen.
   */
  fleetCoverage?: FleetCoverage;
};

/**
 * La prueba de que un umbral común es **normal** para su cadencia.
 *
 * Un cero de un vehículo con un umbral que nadie de su cadencia alcanza no dice
 * nada. Con un umbral que la mayoría sí alcanza, el mismo cero dice que este
 * vehículo no hizo esa maniobra. Esta es la cifra que separa los dos casos, y
 * viaja dentro del perfil —y por eso dentro de la firma— para que cada informe
 * diga contra qué flota se juzgó su cero.
 */
export type FleetCoverage = {
  /** Período con el que se midió la flota, el mismo que firmó el servidor. */
  fromIso: string;
  toIso: string;
  /** Vehículos de la cadencia con al menos `minimumDistanceKm` en el período. */
  vehicles: number;
  /** En cuántos de esos vehículos pudo dispararse cada familia con estos umbrales. */
  firing: {
    velocidadAlta: number;
    frenadas: number;
    aceleraciones: number;
    giros: number;
  };
  minimumDistanceKm: number;
};

/**
 * Perfil de alta frecuencia. Son los umbrales de detección del original, sin
 * tocar un solo número. Lo único que agrega es la regla de posición repetida
 * del ADR-010, que en `LAB_PROFILE` —el de la paridad— queda apagada.
 */
export const PROFILE_HIGH: AnalysisProfile = {
  ...LAB_PROFILE,
  // ADR-010: la única diferencia con el original. Las posiciones repetidas en
  // plena marcha no entran al cálculo; ningún umbral de detección cambia.
  quality: { ...LAB_PROFILE.quality, repeatedFixMinimumSpeedKmh: REPEATED_FIX_MINIMUM_SPEED_KMH },
  regime: "HIGH",
  appliesToIntervalSeconds: [0, CADENCE_REGIME_HIGH_MEDIUM_SECONDS],
  measures: "Aceleración y giro casi instantáneos, con muestreo de 1 a 3 s.",
  calibration: "CALIBRATED",
};

/** Escala una banda de severidad conservando su forma de tres umbrales. */
function scaleBand(
  band: readonly [number, number, number],
  scale: number,
  round1: (value: number) => number,
): readonly [number, number, number] {
  return [round1(band[0] * scale), round1(band[1] * scale), round1(band[2] * scale)];
}

/**
 * Deriva un perfil para una cadencia dada.
 *
 * El criterio provisional es el único defendible sin datos: **conservar la
 * variación de velocidad exigida**, no la aceleración. A 2 s, un umbral de
 * 2,5 m/s² equivale a variar 18 km/h; ese cambio de velocidad sigue siendo un
 * evento notable si ocurre en 5 o en 10 segundos, mientras que exigir la misma
 * aceleración media sería exigir un cambio imposible.
 *
 * Es una hipótesis explícita, no una calibración. Queda marcada `UNCALIBRATED`
 * hasta que el laboratorio la contraste contra la distribución real de la
 * flota, que es la única forma honesta de fijar dónde empieza «brusco».
 */
function deriveProfile(options: {
  id: string;
  regime: CadenceRegime;
  intervalSeconds: number;
  appliesTo: readonly [number, number];
}): AnalysisProfile {
  const referenceSeconds = 2;
  const { intervalSeconds } = options;

  // Δv constante ⇒ el umbral de aceleración escala con la razón de intervalos.
  const scale = referenceSeconds / intervalSeconds;
  const round1 = (value: number) => Math.round(value * 10) / 10;

  return {
    ...LAB_PROFILE,
    profileId: options.id,
    version: 1,
    status: "DRAFT",
    quality: { ...LAB_PROFILE.quality, repeatedFixMinimumSpeedKmh: REPEATED_FIX_MINIMUM_SPEED_KMH },
    longitudinal: {
      ...LAB_PROFILE.longitudinal,
      accelerationThresholdMps2: round1(LAB_PROFILE.longitudinal.accelerationThresholdMps2 * scale),
      brakingThresholdMps2: round1(LAB_PROFILE.longitudinal.brakingThresholdMps2 * scale),
    },
    turns: {
      ...LAB_PROFILE.turns,
      // Igual razonamiento: se conserva el CAMBIO DE RUMBO exigido, no la
      // velocidad angular. Girar 35° sigue siendo girar 35°, tarde lo que tarde.
      minimumYawRateDegreesPerSecond: round1(
        LAB_PROFILE.turns.minimumYawRateDegreesPerSecond * scale,
      ),
    },
    severity: {
      ...LAB_PROFILE.severity,
      acceleration: scaleBand(LAB_PROFILE.severity.acceleration, scale, round1),
      braking: scaleBand(LAB_PROFILE.severity.braking, scale, round1),
      yawRate: scaleBand(LAB_PROFILE.severity.yawRate, scale, round1),
    },
    regime: options.regime,
    appliesToIntervalSeconds: options.appliesTo,
    measures: `Aceleración y giro promediados sobre ${intervalSeconds} s. No es el pico del evento: es su intensidad sostenida.`,
    calibration: "UNCALIBRATED",
  };
}

export const PROFILE_MEDIUM = deriveProfile({
  id: "guardia-lab-5s",
  regime: "MEDIUM",
  intervalSeconds: 5,
  appliesTo: [CADENCE_REGIME_HIGH_MEDIUM_SECONDS, CADENCE_REGIME_MEDIUM_LOW_SECONDS],
});

export const PROFILE_LOW = deriveProfile({
  id: "guardia-lab-10s",
  regime: "LOW",
  intervalSeconds: 10,
  appliesTo: [CADENCE_REGIME_MEDIUM_LOW_SECONDS, MANEUVER_CADENCE_CEILING_SECONDS],
});

export const PROFILE_FAMILY = [PROFILE_HIGH, PROFILE_MEDIUM, PROFILE_LOW] as const;

export type ProfileSelection = {
  profile: AnalysisProfile;
  /** Cadencia medida que llevó a esta elección. `null` si no se pudo medir. */
  measuredIntervalSeconds: number | null;
  /** Por qué se escogió, para que salga en el informe y no sea magia. */
  reason: string;
};

/**
 * Escoge el perfil según la cadencia medida del equipo.
 *
 * Determinístico y total: cualquier entrada produce exactamente un perfil, y
 * el mismo siempre. Sin cadencia medible se usa el perfil de alta frecuencia,
 * que es el conservador — detecta menos, nunca de más.
 */
export function selectProfile(measuredIntervalSeconds: number | null): ProfileSelection {
  if (measuredIntervalSeconds === null || !Number.isFinite(measuredIntervalSeconds)) {
    return {
      profile: PROFILE_HIGH,
      measuredIntervalSeconds: null,
      reason:
        "No se pudo medir la cadencia del equipo en marcha: el período no trae marcha suficiente. Se usa el perfil de alta frecuencia, que es el más exigente: detecta menos, nunca de más.",
    };
  }

  const interval = Math.round(measuredIntervalSeconds * 10) / 10;

  /*
   * Por encima del techo de cadencia no hay perfil que aplique.
   *
   * Antes esto caía en silencio al perfil de 10 s (`?? PROFILE_LOW`). A más de
   * 30 s por punto una maniobra entera cabe entre dos lecturas: lo que el motor
   * marca como aceleración es la diferencia entre dos velocidades sueltas
   * separadas medio minuto o más. Se sigue usando el perfil de 10 s —la
   * velocidad sostenida sí se mide igual—, pero la razón lo dice, y
   * `sampling-quality.ts` declara las tres familias de maniobra sin medición.
   *
   * Corrección del ADR-011: el caso que motivó este techo, «un equipo de 33 a
   * 61 s con 72 aceleraciones por 100 km», no era un equipo lento. En marcha
   * reportaba cada 4 s; los 33 a 61 s eran sus puntos estacionados, y las
   * aceleraciones salían de aplicarle el perfil de 10 s a un equipo de 4 s. El
   * techo sigue valiendo para un equipo que de verdad reporte cada más de 30 s
   * en marcha, y ahora se compara contra la cadencia en marcha.
   */
  if (measuredIntervalSeconds >= MANEUVER_CADENCE_CEILING_SECONDS) {
    return {
      profile: PROFILE_LOW,
      measuredIntervalSeconds,
      reason:
        `En marcha, el equipo reporta un punto cada ${interval} s, por encima de los ` +
        `${MANEUVER_CADENCE_CEILING_SECONDS} s hasta donde llega cualquier perfil. A esa cadencia una ` +
        `maniobra completa cabe entre dos lecturas, así que aceleraciones, frenadas y giros no se pueden ` +
        `medir con este equipo; se aplica el perfil ${PROFILE_LOW.profileId} solo para la velocidad sostenida.`,
    };
  }

  const match =
    PROFILE_FAMILY.find(
      (profile) =>
        profile.appliesToIntervalSeconds !== null &&
        measuredIntervalSeconds >= profile.appliesToIntervalSeconds[0] &&
        measuredIntervalSeconds < profile.appliesToIntervalSeconds[1],
    ) ?? PROFILE_LOW;

  /*
   * Lo que la cadencia decide es QUÉ perfil, no SI está calibrado.
   *
   * Esta razón terminaba en «Sus umbrales son provisionales» porque el perfil del
   * código lo es. Pero `resolveProfile` reemplaza ese perfil por el calibrado
   * cuando hay uno activo, y la respuesta pega las dos razones: el informe decía
   * «provisionales» y en la frase siguiente «calibrados, versión 16». La
   * procedencia de los umbrales la dice `resolveProfile`, que es quien la conoce.
   */
  return {
    profile: match,
    measuredIntervalSeconds,
    reason:
      `En marcha, el equipo reporta un punto cada ${interval} s, ` +
      `así que se aplica el perfil ${match.profileId}. ${match.measures}`,
  };
}

// ---------------------------------------------------------------------------
// Preajustes oficiales del manual GuardIA
// ---------------------------------------------------------------------------


/**
 * Aplica unos umbrales calibrados sobre un perfil del código.
 *
 * ## Por qué esto vive aquí y no en cada lado
 *
 * Esta regla estaba escrita DOS veces: una en el servidor, que arma el perfil
 * con el que se genera el informe, y otra en el laboratorio, que arma el
 * perfil con el que se ve la vista previa. Eran la misma regla copiada, y
 * pasó lo que siempre pasa: al agregar dos umbrales nuevos se actualizó una
 * copia y no la otra. El laboratorio mostraba un resultado y el informe daba
 * otro — que es exactamente lo que el laboratorio existe para que no ocurra.
 *
 * Una sola función, y las dos la llaman. El tipo del parámetro es
 * `TunableThresholds`, el mismo del contrato: agregar una perilla allá y no
 * usarla aquí deja de compilar en vez de perderse en silencio.
 */
export function applyThresholds(base: AnalysisProfile, t: TunableThresholds): AnalysisProfile {
  return {
    ...base,
    longitudinal: {
      ...base.longitudinal,
      accelerationThresholdMps2: t.accelerationThresholdMps2,
      brakingThresholdMps2: t.brakingThresholdMps2,
      minimumSpeedChangeKmh: t.minimumSpeedChangeKmh,
    },
    turns: {
      ...base.turns,
      minimumYawRateDegreesPerSecond: t.minimumYawRateDegreesPerSecond,
      minimumHeadingChangeDegrees: t.minimumHeadingChangeDegrees,
      minimumReferenceSpeedKmh: t.minimumReferenceSpeedKmh,
      lateralAccelerationMps2: t.lateralAccelerationMps2,
    },
    elevatedSpeed: {
      ...base.elevatedSpeed,
      operationalSpeedLimitKmh: t.operationalSpeedLimitKmh,
      operationalSpeedExitKmh: t.operationalSpeedExitKmh,
      sustainedWindowSeconds: t.sustainedWindowSeconds,
    },
    severity: {
      ...base.severity,
      acceleration: t.severityAcceleration,
      braking: t.severityBraking,
      yawRate: t.severityYawRate,
    },
  };
}

/**
 * La inversa de `applyThresholds`: las perillas que un perfil tiene puestas.
 *
 * El tipo de retorno no es adorno: es la guarda contra que vuelva a faltar un
 * campo. Faltaban `lateralAccelerationMps2` y `sustainedWindowSeconds`, y el
 * `.default(0)` del esquema no dejaba que el cliente fallara: los rellenaba con
 * cero, o sea que los pintaba «apagados» en la pantalla. El síntoma fue una
 * vista previa del laboratorio que no coincidía con el informe publicado.
 *
 * Vive junto a su inversa a propósito. Una perilla nueva del contrato hay que
 * agregarla en las dos, y tenerlas a la vista es lo que hace evidente la que
 * falta — el defecto original fue justamente una perilla agregada en un lado y
 * olvidada en el otro.
 *
 * `operationalSpeedExitKmh` está aquí por lo mismo: es una perilla más del
 * contrato, y el tipo de retorno la exige. Olvidarla habría repetido el defecto
 * con la histéresis pintada en 0 en la pantalla mientras el informe la aplica.
 *
 * Las bandas de severidad se copian: el perfil las declara `readonly` para que
 * nadie mute un perfil calibrado, y el contrato las quiere mutables porque
 * `/api/calibration/save` las recorre con `ordered()`. Copiarlas al serializar
 * concilia las dos reglas sin relajar ninguna.
 */
export const tunableThresholdsOf = (profile: AnalysisProfile): TunableThresholds => ({
  accelerationThresholdMps2: profile.longitudinal.accelerationThresholdMps2,
  brakingThresholdMps2: profile.longitudinal.brakingThresholdMps2,
  minimumSpeedChangeKmh: profile.longitudinal.minimumSpeedChangeKmh,
  minimumYawRateDegreesPerSecond: profile.turns.minimumYawRateDegreesPerSecond,
  minimumHeadingChangeDegrees: profile.turns.minimumHeadingChangeDegrees,
  minimumReferenceSpeedKmh: profile.turns.minimumReferenceSpeedKmh,
  lateralAccelerationMps2: profile.turns.lateralAccelerationMps2,
  operationalSpeedLimitKmh: profile.elevatedSpeed.operationalSpeedLimitKmh,
  operationalSpeedExitKmh: profile.elevatedSpeed.operationalSpeedExitKmh,
  sustainedWindowSeconds: profile.elevatedSpeed.sustainedWindowSeconds,
  severityAcceleration: [...profile.severity.acceleration],
  severityBraking: [...profile.severity.braking],
  severityYawRate: [...profile.severity.yawRate],
});

/**
 * Umbrales del manual `manual_guardia_drivescore_v1_..._v3_acc25`, listos para
 * cargarse en el banco de pruebas como punto de partida.
 *
 * Son los valores que Grupo Arcos ya declaró oficiales, no una propuesta mía.
 * Se cargan como preajuste y desde ahí se afinan contra la flota real: es
 * exactamente el flujo que se pidió, «cargarlas y de a poco perfeccionarlas».
 *
 * ## Lo que hay que saber antes de aplicarlos
 *
 * El manual exige `0 < Δt ≤ 5 s` para aceleración y frenada. **Eso significa
 * que el manual está escrito para equipos que reportan cada 5 segundos o
 * menos.** A 10 segundos, los ±2,5 m/s² del manual exigen variar 90 km/h entre
 * dos puntos y el detector no se dispara nunca (ADR-007). No es un defecto del
 * manual: es que esos equipos están fuera de su alcance, y por eso el preajuste
 * se ofrece para los regímenes de alta y media frecuencia.
 *
 * ## Diferencia real con lo que el motor hace hoy
 *
 * El giro del manual **no** es un umbral fijo de °/s. Es una aceleración
 * lateral: `umbral en grados = (3,4 × Δt / v) × 57,2958`, que es exactamente
 * `a_lat = 3,4 m/s²` expresada como ángulo. Depende de la velocidad, y por eso
 * la tabla del manual baja de 35° a 6° cuando la velocidad sube.
 *
 * El motor de hoy usa un mínimo fijo de °/s, que es otra cosa. Implementar el
 * criterio del manual es un cambio del detector, no un cambio de número, y
 * queda anotado aquí para no darlo por hecho: mientras no se haga, el preajuste
 * traduce el criterio del manual al mínimo fijo más cercano y lo dice.
 */
export const MANUAL_OFICIAL_V3 = {
  id: "manual-guardia-v3-acc25",
  label: "Manual oficial GuardIA v3 (acc 2,5)",

  /** Sección 3 del manual. Simétricos a propósito desde la v3. */
  accelerationThresholdMps2: 2.5,
  brakingThresholdMps2: -2.5,
  /** Velocidad mínima de ambos extremos para que el evento cuente. */
  minimumSpeedChangeKmh: 0,
  minimumSpeedForLongitudinalKmh: 10,
  /** `0 < Δt ≤ 5 s`. Fuera de esa ventana no se infiere el evento. */
  maximumMidpointGapSeconds: 5,

  /** Sección 4. Aceleración lateral, no velocidad angular fija. */
  lateralAccelerationMps2: 3.4,
  minimumTurnSpeedKmh: 40,
  minimumHeadingChangeDegrees: 20,
  /** Δt ≤ 2 s pesa 1,0 · 2 < Δt ≤ 5 s pesa 0,5 · más de 5 s no cuenta. */
  sharpTurnMaxGapSeconds: 2,
  strongTurnMaxGapSeconds: 5,
  sharpTurnWeight: 1,
  strongTurnWeight: 0.5,

  /** SpeedLimit v1: lo que entra al score es la velocidad alta OPERATIVA. */
  operationalSpeedLimitKmh: 80,
  operationalSpeedExitKmh: 76,
  /** Referencia legal, con histéresis. NO entra al score sin vía validada. */
  legalReferenceSpeedKmh: 90,
  legalReferenceExitKmh: 86,
} as const;

/**
 * El umbral angular del manual para una velocidad y un Δt dados.
 *
 * `max(dinámico, 20°)`, donde el dinámico sale de la aceleración lateral. Sirve
 * para reproducir la tabla de la sección 4.3 y para comprobarla a mano, que es
 * la única forma de saber que se implementó lo que el manual dice.
 */
export function manualTurnThresholdDegrees(speedKmh: number, gapSeconds: number): number {
  if (speedKmh <= 0 || gapSeconds <= 0) return MANUAL_OFICIAL_V3.minimumHeadingChangeDegrees;
  const speedMps = speedKmh / 3.6;
  const dynamic = (MANUAL_OFICIAL_V3.lateralAccelerationMps2 * gapSeconds / speedMps) * 57.2958;
  return Math.max(dynamic, MANUAL_OFICIAL_V3.minimumHeadingChangeDegrees);
}

/**
 * Velocidad angular mínima equivalente al criterio del manual, en °/s.
 *
 * Es la traducción al parámetro que el motor sí tiene hoy. Depende de la
 * velocidad —el criterio del manual también—, así que se calcula sobre la
 * velocidad de referencia que se le pase. A 40 km/h da el umbral más exigente
 * del rango que el manual puntúa.
 */
export function manualYawRateAt(speedKmh: number): number {
  const speedMps = speedKmh / 3.6;
  return Math.round(((MANUAL_OFICIAL_V3.lateralAccelerationMps2 / speedMps) * 57.2958) * 100) / 100;
}
